import { Form, Formik } from "formik";
import { initialValuesFormModalidade } from "../schemas/initialValuesFormModalidade";
import { validationModalidade } from "../schemas/modalidadeValidated";
import { Stack, TextField, Typography } from "@mui/material";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";

import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import { IModalidade } from "interfaces/eventos";
import { editModalidade, saveModalidade } from "services/modalidade";
import { setDataConfig, setMessage, useMaterialUIController } from "context";
import { keyLocalStorage } from "helpers/constants";
import {
  fieldSx,
  MODALIDADES_ACCENT,
  saveButtonSx,
} from "../modalidades.theme";

interface Props {
  modalidade: IModalidade | null;
  onClose: () => void;
}

export default function FormModalidade({ modalidade, onClose }: Props) {
  const [controller, dispatch] = useMaterialUIController();
  const { dataConfig } = controller;
  const { modalidade: modalidades } = dataConfig;

  const initialValues = initialValuesFormModalidade(modalidade);

  const handleSubmit = async (values: Partial<IModalidade>) => {
    try {
      const payload = { tipo: values.tipo, icone: values.icone };
      let message = "Modalidade criada com sucesso";
      let updatedModalidades;

      if (modalidade) {
        await editModalidade(modalidade.id, payload);
        updatedModalidades = modalidades.map((item: IModalidade) =>
          item.id === modalidade.id ? { ...item, ...payload } : item,
        );
        message = "Modalidade editada com sucesso";
      } else {
        const newModalidade = await saveModalidade(payload);
        updatedModalidades = [...modalidades, newModalidade];
      }

      const updatedData = { ...dataConfig, modalidade: updatedModalidades };
      setDataConfig(dispatch, updatedData);
      localStorage.setItem(
        keyLocalStorage.DEFAULT_CONSTANTS,
        JSON.stringify(updatedData),
      );

      setMessage(dispatch, { open: true, text: message });
      onClose();
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: error instanceof Error ? error.message : "Erro ao salvar modalidade.",
      });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationModalidade}
      onSubmit={(values) => {
        void handleSubmit(values);
      }}
    >
      {({ values, handleChange, handleSubmit, isSubmitting }) => (
        <Form id="form-modalidade" onSubmit={handleSubmit}>
          <MDBox p={3}>
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CategoryOutlinedIcon sx={{ color: MODALIDADES_ACCENT }} />
                <MDTypography variant="button" fontWeight="bold">
                  Dados da modalidade
                </MDTypography>
              </Stack>

              <TextField
                name="tipo"
                label="Tipo de modalidade"
                required
                fullWidth
                size="small"
                value={values.tipo}
                onChange={handleChange}
                placeholder="Ex.: Corrida, Ciclismo, Triathlon"
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />

              <Typography variant="caption" color="text.secondary">
                Este nome aparece na seleção de modalidade ao cadastrar eventos.
              </Typography>
            </Stack>
          </MDBox>

          <MDBox
            px={3}
            py={2}
            display="flex"
            gap={1}
            justifyContent="flex-end"
            borderTop="1px solid"
            borderColor="grey.200"
          >
            <MDButton
              type="button"
              variant="outlined"
              color="dark"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancelar
            </MDButton>
            <MDButton
              type="submit"
              variant="gradient"
              color="dark"
              disabled={isSubmitting}
              sx={saveButtonSx}
            >
              {isSubmitting
                ? "Salvando…"
                : modalidade
                  ? "Salvar alterações"
                  : "Criar modalidade"}
            </MDButton>
          </MDBox>
        </Form>
      )}
    </Formik>
  );
}
